import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {SupplierUser, PermissionLevel, MODULES, MODULE_LABELS, PERMISSION_LABELS} from '../core/_models'
import {createUser, updateUser, updateUserPermissions} from '../core/_requests'
import {useAuth} from '../../../../modules/auth'

interface UserEditModalProps {
  user?: SupplierUser | null
  onClose: () => void
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  user,
  onClose,
}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user' as 'admin' | 'user',
  })
  const [permissions, setPermissions] = useState<Record<string, PermissionLevel>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: '',
    isValid: false
  })

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
      })
      setPermissions(user.permissions || {})
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
      })
      // Set default permissions for new user
      const defaultPermissions: Record<string, PermissionLevel> = {}
      Object.values(MODULES).forEach(module => {
        defaultPermissions[module] = 'NONE'
      })
      setPermissions(defaultPermissions)
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setErrorMessage('')

    // Client-side validation
    if (!formData.email || !formData.email.trim()) {
      setErrorMessage('Email address is required.')
      setIsLoading(false)
      return
    }

    if (!formData.name || !formData.name.trim()) {
      setErrorMessage('Name is required.')
      setIsLoading(false)
      return
    }

    if (!user && (!formData.password || !formData.password.trim())) {
      setErrorMessage('Password is required.')
      setIsLoading(false)
      return
    }

    // Validate password strength for new users
    if (!user && formData.password && !passwordStrength.isValid) {
      setErrorMessage(passwordStrength.feedback)
      setIsLoading(false)
      return
    }

    // Admins bypass per-module permissions (hasModuleAccess short-circuits for role==='admin'), so an
    // admin carries an EMPTY permissions map — and the /permissions endpoint validates `required|array`,
    // rejecting {} with "The permissions field is required". Only sync per-module permissions for the
    // 'user' role (whose matrix always populates every module); skip the call entirely for admins.
    const syncPermissions = formData.role === 'user'

    try {
      if (user) {
        // Update existing user
        await updateUser(user.supplier_user_id, formData, currentUser?.token, intl.locale)
        if (syncPermissions) {
          await updateUserPermissions(user.supplier_user_id, {permissions}, currentUser?.token, intl.locale)
        }
      } else {
        // Create new user
        const response = await createUser(formData, currentUser?.token, intl.locale)
        // After creating user, update permissions (user role only)
        if (response?.supplier_user_id && syncPermissions) {
          await updateUserPermissions(response.supplier_user_id, {permissions}, currentUser?.token, intl.locale)
        }
      }
      onClose()
    } catch (error: any) {
      console.error('Error saving user:', error)
      
      // Handle specific error cases
      if (error?.response?.data?.status_code === 'email_exists') {
        setErrorMessage('This email address is already in use. Please choose a different email.')
      } else if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message)
      } else {
        setErrorMessage('An error occurred while saving the user. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handlePermissionChange = (module: string, level: PermissionLevel) => {
    setPermissions(prev => ({
      ...prev,
      [module]: level,
    }))
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value
    setFormData(prev => ({...prev, email}))
    
    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage('')
    }
  }

  const validatePassword = (password: string) => {
    const minLength = 8
    const hasUpperCase = /[A-Z]/.test(password)
    const hasLowerCase = /[a-z]/.test(password)
    const hasNumbers = /\d/.test(password)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    
    let score = 0
    let feedback = []
    
    if (password.length >= minLength) {
      score += 1
    } else {
      feedback.push(`At least ${minLength} characters`)
    }
    
    if (hasUpperCase) {
      score += 1
    } else {
      feedback.push('One uppercase letter')
    }
    
    if (hasLowerCase) {
      score += 1
    } else {
      feedback.push('One lowercase letter')
    }
    
    if (hasNumbers) {
      score += 1
    } else {
      feedback.push('One number')
    }
    
    if (hasSpecialChar) {
      score += 1
    } else {
      feedback.push('One special character')
    }
    
    const isValid = score === 5
    
    return {
      score,
      feedback: feedback.length > 0 ? `Must include: ${feedback.join(', ')}` : 'Strong password',
      isValid
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value
    setFormData(prev => ({...prev, password}))
    
    // Validate password strength
    const validation = validatePassword(password)
    setPasswordStrength(validation)
    
    // Clear error message when user starts typing
    if (errorMessage) {
      setErrorMessage('')
    }
  }

  const handleRoleChange = (role: 'admin' | 'user') => {
    setFormData(prev => ({...prev, role}))
    
    // Auto-set permissions based on role
    const newPermissions: Record<string, PermissionLevel> = {}
    Object.values(MODULES).forEach(module => {
      newPermissions[module] = role === 'admin' ? 'EDIT' : 'NONE'
    })
    setPermissions(newPermissions)
  }

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050}}>
      <div className='modal-dialog modal-lg modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              {user ? intl.formatMessage({id: 'USERS.EDIT_USER'}) : intl.formatMessage({id: 'USERS.ADD_USER'})}
            </h5>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {errorMessage && (
                <div className='alert alert-danger mb-3' role='alert'>
                  {errorMessage}
                </div>
              )}
              <div className='row mb-3'>
                <div className='col-md-6'>
                  <label className='form-label'>{intl.formatMessage({id: 'USERS.NAME'})}</label>
                  <input
                    type='text'
                    className='form-control'
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    required
                  />
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>{intl.formatMessage({id: 'USERS.EMAIL'})}</label>
                  <input
                    type='email'
                    className='form-control'
                    value={formData.email}
                    onChange={handleEmailChange}
                    required
                  />
                </div>
              </div>

              <div className='row mb-3'>
                <div className='col-md-6'>
                  <label className='form-label'>{intl.formatMessage({id: 'USERS.PASSWORD'})}</label>
                  <input
                    type='password'
                    className={`form-control ${!user && formData.password && !passwordStrength.isValid ? 'is-invalid' : ''}`}
                    value={formData.password}
                    onChange={handlePasswordChange}
                    required={!user}
                    placeholder={user ? 'Leave blank to keep current password' : 'Enter a strong password'}
                  />
                  {!user && formData.password && (
                    <div className='mt-2'>
                      <div className='d-flex align-items-center mb-1'>
                        <small className={`me-2 ${passwordStrength.isValid ? 'text-success' : 'text-warning'}`}>
                          Password Strength:
                        </small>
                        <div className='progress flex-grow-1' style={{height: '4px'}}>
                          <div 
                            className={`progress-bar ${passwordStrength.score <= 2 ? 'bg-danger' : passwordStrength.score <= 3 ? 'bg-warning' : 'bg-success'}`}
                            style={{width: `${(passwordStrength.score / 5) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                      <small className={`${passwordStrength.isValid ? 'text-success' : 'text-muted'}`}>
                        {passwordStrength.feedback}
                      </small>
                    </div>
                  )}
                </div>
                <div className='col-md-6'>
                  <label className='form-label'>{intl.formatMessage({id: 'USERS.ROLE'})}</label>
                  <select
                    className='form-select'
                    value={formData.role}
                    onChange={(e) => handleRoleChange(e.target.value as 'admin' | 'user')}
                  >
                    <option value='user'>{intl.formatMessage({id: 'USERS.USER_ROLE_LABEL'})}</option>
                    <option value='admin'>{intl.formatMessage({id: 'USERS.ADMIN_ROLE'})}</option>
                  </select>
                </div>
              </div>

              {formData.role === 'user' && (
                <div className='mb-3'>
                  <h6 className='mb-3'>{intl.formatMessage({id: 'USERS.PERMISSIONS'})}</h6>
                  <div className='table-responsive'>
                    <table className='table table-bordered table-sm' style={{borderCollapse: 'collapse', width: '100%'}}>
                      <tbody>
                        {/* Header Row */}
                        <tr style={{backgroundColor: '#f8f9fa', fontWeight: 'bold'}}>
                          <td style={{minWidth: '150px', padding: '8px', border: '1px solid #dee2e6'}}>{intl.formatMessage({id: 'USERS.MODULE'})}</td>
                          <td className='text-center' style={{minWidth: '100px', padding: '8px', border: '1px solid #dee2e6'}}>{intl.formatMessage({id: 'USERS.NO_ACCESS'})}</td>
                          <td className='text-center' style={{minWidth: '100px', padding: '8px', border: '1px solid #dee2e6'}}>{intl.formatMessage({id: 'USERS.FULL_ACCESS'})}</td>
                        </tr>
                        {/* Data Rows */}
                        {Object.values(MODULES).map(module => {
                          console.log('Rendering module:', module, 'Label:', MODULE_LABELS[module])
                          return (
                          <tr key={module}>
                            <td className='fw-semibold' style={{padding: '8px', border: '1px solid #dee2e6'}}>{MODULE_LABELS[module]}</td>
                            <td className='text-center' style={{padding: '8px', border: '1px solid #dee2e6'}}>
                              <input
                                type='radio'
                                name={`permission_${module}`}
                                className='form-check-input'
                                checked={permissions[module] === 'NONE'}
                                onChange={() => handlePermissionChange(module, 'NONE')}
                              />
                            </td>
                            <td className='text-center' style={{padding: '8px', border: '1px solid #dee2e6'}}>
                              <input
                                type='radio'
                                name={`permission_${module}`}
                                className='form-check-input'
                                checked={permissions[module] === 'EDIT'}
                                onChange={() => handlePermissionChange(module, 'EDIT')}
                              />
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className='modal-footer d-flex justify-content-end gap-2'>
              <button type='button' className='btn btn-secondary' onClick={onClose}>
                {intl.formatMessage({id: 'COMMON.CANCEL'})}
              </button>
              <button type='submit' className='btn btn-primary' disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2'></span>
                    {intl.formatMessage({id: 'COMMON.SAVING'})}
                  </>
                ) : (
                  intl.formatMessage({id: 'COMMON.SAVE'})
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export {UserEditModal}
